import { DateTime } from "@bps/date-time";
import { Spinner } from "@bps/fluent-ui";
import {
  ClinicalDataType,
  GRCSQuestionnaireResponseDto,
  ObservationType,
  QuestionnaireType
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import {
  getClinicalDataLastUpdatedDate,
  getClinicalDataLastUpdatedUserId
} from "@stores/clinical/utils/clinical.utils.ts";
import { RootStore } from "@stores/root/RootStore.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";

import { ClinicalToolView } from "../clinical-tool-view/ClinicalToolView.tsx";
import { findClinicalToolAnswerText } from "../utils/ClinicalToolAnswerFinder.ts";
import { GRCSBaselineForm } from "./GRCSBaselineForm.tsx";
import { CLINICAL_TOOLS_FORM_HEADING } from "./GRCSForm.types.ts";
import { GRCSFormViewModel } from "./GRCSFormViewModel.ts";

export interface GRCSQuestionnaireViewProps {
  encounterId: string;
}

export const GRCSFormView: React.FunctionComponent<
  GRCSQuestionnaireViewProps
> = ({ encounterId }) => {
  const getGRCSViewData =
    () =>
    async ({ clinical }: RootStore): Promise<GRCSFormViewModel> => {
      const questionnaire = await clinical.getQuestionnaires(
        QuestionnaireType.GRCSV1
      );

      const savedData = (
        await clinical.getEncounterClinicalData({
          encounterId,
          types: [ClinicalDataType.GRCS]
        })
      )[
        ClinicalDataType.GRCS.toLocaleLowerCase()
      ] as GRCSQuestionnaireResponseDto;

      const answers = savedData
        ? savedData.items.map(i => ({
            questionId: `${i.questionnaireItemId}`,
            answerValue: i.answer,
            answerText: findClinicalToolAnswerText(
              i.questionnaireItemId,
              i.answer!,
              questionnaire
            )
          }))
        : [];

      const result = await clinical.getObservations({
        patientId: clinical.activeRecordPatientId,
        encounterIds: [encounterId],
        types: [ObservationType.GRCS]
      });

      const observations = result.results;

      if (observations.length !== 1)
        throw new Error(
          `Unable to retrieve ${ObservationType.GRCS} observation data`
        );

      const summary =
        observations[0].summary ?? observations[0].values[0].value.toString();

      const isConfidential = !!savedData.secGroupId;

      const lastUpdatedDate = getClinicalDataLastUpdatedDate(savedData);

      const lastUpdatedUserId = getClinicalDataLastUpdatedUserId(savedData);

      return {
        observations,
        answers,
        summary,
        questionnaire,
        side: savedData.side,
        diagnosis: savedData.diagnosis,
        encounterDate: savedData.createLog?.createdDateTime,
        isConfidential,
        lastUpdatedDate,
        lastUpdatedUserId
      };
    };

  const title = (encounterDate: string | undefined) =>
    `${CLINICAL_TOOLS_FORM_HEADING} - ${DateTime.fromISOOrNow(
      encounterDate
    ).toDayDefaultFormat()}`;

  return (
    <DataFetcher<GRCSFormViewModel>
      fetch={getGRCSViewData()}
      fallback={<Spinner />}
      key={encounterId}
    >
      {({
        answers,
        summary,
        questionnaire,
        side,
        diagnosis,
        encounterDate,
        isConfidential,
        lastUpdatedDate,
        lastUpdatedUserId
      }) => (
        <ClinicalToolView
          title={title(encounterDate)}
          summary={summary}
          isConfidential={isConfidential}
          lastUpdatedDate={lastUpdatedDate}
          lastUpdatedUserId={lastUpdatedUserId}
        >
          <GRCSBaselineForm
            questionnaire={questionnaire}
            diagnosis={diagnosis}
            viewData={{
              answer: answers[0].answerValue,
              side
            }}
          />
        </ClinicalToolView>
      )}
    </DataFetcher>
  );
};
